'use client';

import Link from 'next/link';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ProfessorLinkPreview } from '@/app/professors/professorProfileContent';

export default function ProfessorLink({ preview }: { preview: ProfessorLinkPreview }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const tooltipId = useId();

  const updatePosition = useCallback(() => {
    const anchor = linkRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    const left = Math.max(12, Math.min(window.innerWidth - width - 12, rect.left));
    const estimatedHeight = 154;
    const top = rect.bottom + 8 + estimatedHeight <= window.innerHeight
      ? rect.bottom + 8
      : Math.max(12, rect.top - estimatedHeight - 8);
    setPosition({ left, top });
  }, []);

  const show = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    setOpen(true);
    updatePosition();
  }, [updatePosition]);

  const hide = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, []);

  useEffect(() => {
    if (!open) return;
    const reposition = () => updatePosition();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, updatePosition]);

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <>
      <Link
        ref={linkRef}
        href={preview.href}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex min-h-11 items-center font-medium text-slate-900 underline decoration-slate-400 underline-offset-4 hover:text-red-700 hover:decoration-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:text-slate-100 dark:hover:text-red-300 md:min-h-0"
      >
        {preview.displayName}
      </Link>
      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className="pointer-events-none fixed z-[90] w-[min(20rem,calc(100vw-1.5rem))] rounded-card border border-slate-200 bg-white p-4 text-left shadow-e3 dark:border-slate-700 dark:bg-slate-900"
              style={position}
            >
              <span className="block text-sm font-bold text-slate-950 dark:text-white">{preview.displayName}</span>
              <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                {preview.canonical ? 'Professor Wiki preview' : 'SIRS instructor lookup'}
              </span>
              <span className="mt-2 block text-sm leading-5 text-slate-600 dark:text-slate-300">{preview.summary}</span>
              <span className="mt-2 block text-xs font-semibold text-slate-500 dark:text-slate-400">{preview.meta}</span>
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
